import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/ClientLayout';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Calendar, RefreshCw, Home, DollarSign, MoreHorizontal, Play, Pause, Trash2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import axios from 'axios';

const ServicePlansPage = () => {
  const { token, API } = useAuth();
  const [plans, setPlans] = useState([]);
  const [properties, setProperties] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [jobCount, setJobCount] = useState(4);
  const [formData, setFormData] = useState({
    property_id: '',
    customer_id: '',
    service_type: '',
    frequency: 'weekly',
    custom_interval_days: 7,
    price: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    notes: ''
  });

  const serviceTypes = [
    'Lawn Mowing',
    'Hedge Trimming',
    'Leaf Removal',
    'Snow Removal',
    'Fertilization',
    'Weed Control',
    'Aeration',
    'Mulching',
    'Full Maintenance',
    'Other'
  ];

  const frequencyOptions = [
    { value: 'weekly', label: 'Weekly', description: 'Every 7 days' },
    { value: 'biweekly', label: 'Bi-Weekly', description: 'Every 14 days' },
    { value: 'monthly', label: 'Monthly', description: 'Every 30 days' },
    { value: 'seasonal', label: 'Seasonal', description: 'Every 90 days' },
    { value: 'custom', label: 'Custom', description: 'Set custom interval' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansRes, propertiesRes, customersRes] = await Promise.all([
        axios.get(`${API}/api/service-plans`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/properties`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/customers`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPlans(plansRes.data.service_plans || []);
      setProperties(propertiesRes.data.properties || []);
      setCustomers(customersRes.data.customers || []);
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        custom_interval_days: formData.frequency === 'custom' ? parseInt(formData.custom_interval_days) : null,
        end_date: formData.end_date || null,
        notes: formData.notes || null
      };
      
      await axios.post(`${API}/api/service-plans`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Service plan created successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create service plan');
    }
  };

  const handleGenerateJobs = async () => {
    if (!selectedPlan) return;
    try {
      const response = await axios.post(
        `${API}/api/service-plans/${selectedPlan.id}/generate-jobs?count=${jobCount}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Generated ${response.data.jobs.length} jobs`);
      setGenerateDialogOpen(false);
      setSelectedPlan(null);
      setJobCount(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate jobs');
    }
  };

  const resetForm = () => {
    setFormData({
      property_id: '',
      customer_id: '',
      service_type: '',
      frequency: 'weekly',
      custom_interval_days: 7,
      price: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      notes: ''
    });
  };

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return property?.address || 'Unknown Property';
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Unknown Customer';
  };

  const getFrequencyLabel = (frequency, customDays) => {
    const option = frequencyOptions.find(f => f.value === frequency);
    if (frequency === 'custom' && customDays) {
      return `Every ${customDays} days`;
    }
    return option?.label || frequency;
  };

  const filteredPlans = plans.filter(plan =>
    getPropertyName(plan.property_id).toLowerCase().includes(searchQuery.toLowerCase()) ||
    getCustomerName(plan.customer_id).toLowerCase().includes(searchQuery.toLowerCase()) ||
    plan.service_type?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter properties by selected customer
  const filteredProperties = formData.customer_id 
    ? properties.filter(p => p.customer_id === formData.customer_id)
    : properties;

  return (
    <ClientLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Service Plans</h1>
            <p className="text-gray-500 mt-1">Manage recurring service schedules for your properties</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Plans</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {plans.filter(p => p.status === 'active').length}
                  </p>
                </div>
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <RefreshCw className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Properties Covered</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {[...new Set(plans.map(p => p.property_id))].length}
                  </p>
                </div>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Home className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Weekly Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${plans.filter(p => p.status === 'active' && p.frequency === 'weekly')
                      .reduce((sum, p) => sum + (p.price || 0), 0).toFixed(0)}
                  </p>
                </div>
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Next Service</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {plans.length > 0 
                      ? new Date(Math.min(...plans.map(p => new Date(p.next_service_date || p.start_date)))).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : '-'}
                  </p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Plans List */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search plans..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-plans-input"
                />
              </div>
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90" data-testid="add-plan-btn">
                    <Plus className="h-4 w-4 mr-2" /> New Service Plan
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create Service Plan</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Customer *</Label>
                      <Select 
                        value={formData.customer_id} 
                        onValueChange={(value) => setFormData({ ...formData, customer_id: value, property_id: '' })}
                      >
                        <SelectTrigger data-testid="plan-customer-select">
                          <SelectValue placeholder="Select customer" />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map(customer => (
                            <SelectItem key={customer.id} value={customer.id}>
                              {customer.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Property *</Label>
                      <Select 
                        value={formData.property_id} 
                        onValueChange={(value) => setFormData({ ...formData, property_id: value })}
                        disabled={!formData.customer_id}
                      >
                        <SelectTrigger data-testid="plan-property-select">
                          <SelectValue placeholder={formData.customer_id ? "Select property" : "Select customer first"} />
                        </SelectTrigger>
                        <SelectContent>
                          {filteredProperties.map(property => (
                            <SelectItem key={property.id} value={property.id}>
                              {property.address}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Service Type *</Label>
                      <Select 
                        value={formData.service_type} 
                        onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                      >
                        <SelectTrigger data-testid="plan-service-type-select">
                          <SelectValue placeholder="Select service type" />
                        </SelectTrigger>
                        <SelectContent>
                          {serviceTypes.map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Frequency *</Label>
                      <Select 
                        value={formData.frequency} 
                        onValueChange={(value) => setFormData({ ...formData, frequency: value })}
                      >
                        <SelectTrigger data-testid="plan-frequency-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {frequencyOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label} - {option.description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {formData.frequency === 'custom' && (
                      <div className="space-y-2">
                        <Label>Custom Interval (days) *</Label>
                        <Input
                          type="number"
                          min="1"
                          value={formData.custom_interval_days}
                          onChange={(e) => setFormData({ ...formData, custom_interval_days: e.target.value })}
                          data-testid="plan-custom-interval-input"
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Price per Service *</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.price}
                          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                          className="pl-10"
                          placeholder="0.00"
                          data-testid="plan-price-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start Date *</Label>
                        <Input
                          type="date"
                          value={formData.start_date}
                          onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                          data-testid="plan-start-date-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>End Date (Optional)</Label>
                        <Input
                          type="date"
                          value={formData.end_date}
                          onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                          data-testid="plan-end-date-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Any special instructions..."
                        rows={3}
                        data-testid="plan-notes-input"
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        className="bg-primary hover:bg-primary/90"
                        disabled={!formData.property_id || !formData.customer_id || !formData.service_type || !formData.price}
                        data-testid="plan-submit-btn"
                      >
                        Create Plan
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>

          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : filteredPlans.length === 0 ? (
              <div className="text-center py-12">
                <RefreshCw className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">No service plans found</p>
                <Button variant="outline" onClick={() => setDialogOpen(true)}>
                  Create Your First Service Plan
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredPlans.map((plan) => (
                  <div 
                    key={plan.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    data-testid={`plan-${plan.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        plan.status === 'active' ? 'bg-emerald-100' : 'bg-gray-200'
                      }`}>
                        <RefreshCw className={`h-5 w-5 ${
                          plan.status === 'active' ? 'text-emerald-600' : 'text-gray-500'
                        }`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">{plan.service_type}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            plan.status === 'active' 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-gray-200 text-gray-600'
                          }`}>
                            {plan.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{getPropertyName(plan.property_id)}</p>
                        <p className="text-sm text-gray-500">{getCustomerName(plan.customer_id)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">${plan.price?.toFixed(2)}</p>
                        <p className="text-sm text-gray-500">{getFrequencyLabel(plan.frequency, plan.custom_interval_days)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-900">Next Service</p>
                        <p className="text-sm text-gray-500">
                          {new Date(plan.next_service_date || plan.start_date).toLocaleDateString()}
                        </p>
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`plan-actions-${plan.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setSelectedPlan(plan); setGenerateDialogOpen(true); }}>
                            <Play className="h-4 w-4 mr-2" />
                            Generate Jobs
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Pause className="h-4 w-4 mr-2" />
                            {plan.status === 'active' ? 'Pause Plan' : 'Activate Plan'}
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Plan
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Generate Jobs Dialog */}
        <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Generate Jobs</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {selectedPlan && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-medium">{selectedPlan.service_type}</p>
                  <p className="text-sm text-gray-600">{getPropertyName(selectedPlan.property_id)}</p>
                  <p className="text-sm text-gray-500">{getFrequencyLabel(selectedPlan.frequency, selectedPlan.custom_interval_days)}</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Number of Jobs to Generate</Label>
                <Select value={jobCount.toString()} onValueChange={(v) => setJobCount(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 4, 8, 12].map(num => (
                      <SelectItem key={num} value={num.toString()}>{num} jobs</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Jobs will be scheduled starting from the next service date based on the plan frequency.
                </p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-primary hover:bg-primary/90" 
                  onClick={handleGenerateJobs}
                  data-testid="generate-jobs-btn"
                >
                  Generate Jobs
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </ClientLayout>
  );
};

export default ServicePlansPage;
